import * as React from 'react';
import { Box, BoxProps } from '@chakra-ui/react';

export interface CollapsibleRootProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface CollapsibleContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CollapsibleContext = React.createContext<CollapsibleContextValue | undefined>(undefined);

export const CollapsibleRoot = React.forwardRef<HTMLDivElement, CollapsibleRootProps>(
  ({ children, open: controlledOpen, defaultOpen = true, onOpenChange }, ref) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
    const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;

    const handleOpenChange = React.useCallback(
      (newOpen: boolean) => {
        if (controlledOpen === undefined) {
          setUncontrolledOpen(newOpen);
        }
        onOpenChange?.(newOpen);
      },
      [controlledOpen, onOpenChange]
    );

    const value = React.useMemo(
      () => ({
        open,
        onOpenChange: handleOpenChange,
      }),
      [open, handleOpenChange]
    );

    return (
      <CollapsibleContext.Provider value={value}>
        <Box ref={ref}>{children}</Box>
      </CollapsibleContext.Provider>
    );
  }
);

CollapsibleRoot.displayName = 'CollapsibleRoot';

export interface CollapsibleTriggerProps extends BoxProps {
  asChild?: boolean;
}

export const CollapsibleTrigger = React.forwardRef<HTMLElement, CollapsibleTriggerProps>(
  ({ children, asChild, onClick, ...props }, ref) => {
    const context = React.useContext(CollapsibleContext);
    if (!context) {
      throw new Error('CollapsibleTrigger must be used within CollapsibleRoot');
    }

    const handleClick = (e: React.MouseEvent) => {
      onClick?.(e as React.MouseEvent<HTMLDivElement>);
      context.onOpenChange(!context.open);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        ref,
        onClick: handleClick,
        'aria-expanded': context.open,
        ...props,
      });
    }

    return (
      <Box ref={ref} onClick={handleClick} cursor="pointer" aria-expanded={context.open} {...props}>
        {children}
      </Box>
    );
  }
);

CollapsibleTrigger.displayName = 'CollapsibleTrigger';

export interface CollapsibleContentProps extends BoxProps {
  forceMount?: boolean;
}

export const CollapsibleContent = React.forwardRef<HTMLDivElement, CollapsibleContentProps>(
  ({ children, forceMount, ...props }, ref) => {
    const context = React.useContext(CollapsibleContext);
    if (!context) {
      throw new Error('CollapsibleContent must be used within CollapsibleRoot');
    }

    if (!forceMount && !context.open) {
      return null;
    }

    return (
      <Box ref={ref} hidden={!context.open} {...props}>
        {children}
      </Box>
    );
  }
);

CollapsibleContent.displayName = 'CollapsibleContent';

// Convenience exports
export const Collapsible = {
  Root: CollapsibleRoot,
  Trigger: CollapsibleTrigger,
  Content: CollapsibleContent,
};
